import { z } from "zod";
import { healthBehaviourSchema } from "./health-behaviours.js";
import { streakPhrase } from "./interpret/observation.js";
import { snapshotDrillIdSchema } from "./snapshot-drills.js";
import { snapshotFindingSchema } from "./snapshot-findings.js";
import { snapshotGoalSchema } from "./snapshot-goal.js";
import { snapshotLevelSchema } from "./snapshot-levels.js";
// The drill-in behind each Snapshot tab: one headline reading, the readings
// that went into it, each against the member's own band, and a trend. Every
// value is a real signal or null; a null renders as "Not measured", never 0.
export const SNAPSHOT_DETAIL_RANGES = [7, 30, 90];
export const snapshotDetailRangeSchema = z.union([z.literal(7), z.literal(30), z.literal(90)]);
export { SNAPSHOT_DRILL_FOR_BEHAVIOUR, SNAPSHOT_DRILL_PILLAR, snapshotDrillIdSchema, } from "./snapshot-drills.js";
export const snapshotBandSchema = z.object({ lo: z.number(), hi: z.number(), med: z.number() });
export const snapshotVerdictSchema = z.enum(["in", "under", "over"]);
/** The stages a night is drawn in, whatever integer a provider used for them. */
export const sleepStageSchema = z.enum(["deep", "rem", "light", "awake"]);
export const snapshotDetailReadingSchema = z.object({
    key: z.string(),
    label: z.string(),
    value: z.number().nullable(),
    /** The value as the member reads it: "7h 12m", "44 ms". Null when not measured. */
    display: z.string().nullable(),
    band: snapshotBandSchema.nullable(),
    bandDisplay: z.string().nullable(),
    verdict: snapshotVerdictSchema.nullable(),
    source: z.string().nullable(),
    /** Whether the label takes a plural verb, so copy reads "steps were", not "was". */
    plural: z.boolean().optional(),
    /**
     * The day behind this reading is still being counted, so the value is a
     * running total and the band is a whole day's. Copy has to say so, or it
     * sets what the member has done by breakfast against what they usually do
     * by bedtime.
     */
    inProgress: z.boolean().optional(),
    /** The same reading on each of the last few days, newest first, so index 0 is today and index 1 is yesterday. */
    recent: z
        .array(z.object({
        value: z.number().nullable(),
        display: z.string().nullable(),
        verdict: snapshotVerdictSchema.nullable(),
    }))
        .optional(),
});
/** How far back the readings breakdown can step. A week is what a member recalls. */
export const SNAPSHOT_READING_RECENT_DAYS = 7;
/**
 * One of the member's days, newest first. A timeline reads down the page in the
 * order the days happened, with the reading and whatever else was true that day
 * on the same row, so cause and effect sit together rather than in two blocks.
 */
export const snapshotDaySchema = z.object({
    /** Local day key, so the row can be named in the member's own week. */
    day: z.string(),
    /** The drill's own reading that day, already formatted. Null is a day it did not report. */
    display: z.string().nullable(),
    /** Where the reading sat against the member's usual, when there is a band. */
    verdict: z.enum(["under", "in", "over"]).nullable(),
    /** What else was outside its usual that day. */
    context: z.array(z.string()),
});
export const snapshotTrendSchema = z
    .object({
    label: z.string(),
    /** Glossary key for the heading, when the label is a term. */
    key: z.string().nullable(),
    /**
     * Days something else was outside its own usual, indexed into `series`. Marks
     * only, never every day: a lane that marks a quiet month is noise, and the
     * chart states co-occurrence, never cause.
     */
    context: z.array(z.object({
        day: z.number().int(),
        label: z.string(),
        kind: z.enum(["calendar", "weather"]),
    })),
    unit: z.string(),
    dp: z.number().int().min(0).max(2),
    /** One entry per day of the range, oldest first; null where nothing reported. */
    series: z.array(z.number().nullable()),
    /**
     * Where each day sat against the band, in step with `series`. The clients
     * draw this rather than derive it, so the bar colour, the hover readout and
     * the timeline row cannot disagree. A day still being counted is null: a
     * running total has nothing to sit above or below yet, and judging one
     * against a whole day's band called two hours of sitting by ten in the
     * morning "under your usual", every morning.
     */
    verdicts: z.array(snapshotVerdictSchema.nullable()),
    band: snapshotBandSchema.nullable(),
    /** The run of days ending today that sat the same side of the band. */
    run: z.object({ verdict: snapshotVerdictSchema, days: z.number().int().min(2) }).nullable(),
    /**
     * How the member's own usual range has moved: today's band beside the band
     * from two months back. Null until both windows hold enough of their days.
     */
    drift: z.object({ now: z.string(), was: z.string(), since: z.string() }).nullable(),
    /** The member's own target for this reading, when they have set one. */
    target: z
        .object({
        value: z.number(),
        /** The target as the member reads it, with no framing: "7h", "7,500". */
        label: z.string(),
    })
        .nullable(),
})
    // Every consumer indexes the two in lockstep, so a short array would judge
    // the wrong day rather than fail.
    .refine((t) => t.verdicts.length === t.series.length, {
    message: "verdicts must carry one entry per day of series",
    path: ["verdicts"],
});
export const snapshotDrillSchema = z.object({
    /** The member's recent days, newest first, for the timeline. */
    days: z.array(snapshotDaySchema),
    id: snapshotDrillIdSchema,
    label: z.string(),
    pillar: healthBehaviourSchema.nullable(),
    headline: snapshotDetailReadingSchema,
    window: z.string(),
    contributors: z.array(snapshotDetailReadingSchema),
    trend: snapshotTrendSchema,
    calendar: z
        .object({
        bookedMinutes: z.number(),
        days: z.number(),
        backToBack: z.number(),
        /** The longest unbroken run of meetings, in minutes. */
        longestRunMinutes: z.number(),
        /** Time between meetings inside a day; overnight is not focus time. */
        freeMinutes: z.number(),
        longestFreeMinutes: z.number(),
        /** The week's heaviest day, said once. */
        say: z.string().nullable(),
    })
        .nullable(),
    /** Stage samples across the night, oldest first, when the device sends them. */
    hypnogram: z
        .object({
        from: z.string(),
        to: z.string(),
        samples: z.array(z.object({ at: z.string(), stage: sleepStageSchema })),
    })
        .nullable(),
    /** Minutes in each heart-rate zone, when the device sends them. */
    zones: z
        .array(z.object({ zone: z.number().int(), label: z.string(), minutes: z.number() }))
        .nullable(),
});
export const snapshotConditionIconSchema = z.enum([
    "temperature",
    "night",
    "pollen",
    "air",
    "pressure",
    "sunset",
    "daylight",
    "uv",
]);
export const snapshotConditionSchema = z.object({
    icon: snapshotConditionIconSchema,
    /** The glossary key, so a chip carrying jargon can explain itself. */
    key: z.string(),
    label: z.string(),
    value: z.string(),
    note: z.string(),
    /** The tabs this condition has a bearing on; "overview" is the summary. */
    tabs: z.array(z.string()),
});
export const snapshotSourceDetailSchema = z.object({
    name: z.string(),
    /** The service it reaches us through, when it is not direct. */
    via: z.string().nullable(),
    live: z.boolean(),
    lastSeenAt: z.string().nullable(),
    provides: z.array(z.string()),
    /** Readings this source can send that have not arrived yet. */
    pending: z.array(z.string()),
    /** Where it sits in the read order; null when it measures nothing. */
    rank: z.number().nullable(),
    /** True when it relays another device's reading rather than taking one. */
    relay: z.boolean(),
});
/**
 * A metric two devices both reported on the same day. Snapshot keeps every
 * source and reads the most recent one, so the row states both numbers and
 * which of them the page used.
 */
export const snapshotConflictSchema = z.object({
    metric: z.string(),
    used: z.object({ source: z.string(), display: z.string() }),
    other: z.object({ source: z.string(), display: z.string() }),
    /** Why the shown reading is the one this page reads first. */
    why: z.string().nullable(),
    day: z.string(),
});
export const SNAPSHOT_CONFLICT_RULE = "Two devices can report one reading. We show the dedicated wearable and store the other.";
export const snapshotConfounderSchema = z.object({
    label: z.string(),
    from: z.string(),
    to: z.string().nullable(),
    affects: z.array(healthBehaviourSchema),
});
/** The reading the week turns on: the one furthest outside its own band. */
export const snapshotSharpestSchema = snapshotDetailReadingSchema.extend({
    drill: snapshotDrillIdSchema,
});
/**
 * A composite: one number standing for several readings, with the days behind
 * it and a line saying what those readings did. Never a diagnosis.
 */
export const snapshotCompositeSchema = z.object({
    /** The glossary key, which is also the card's identity. */
    key: z.string(),
    label: z.string(),
    value: z.number(),
    display: z.string(),
    /** One entry per day, oldest first; null where nothing reported. */
    series: z.array(z.number().nullable()),
    read: z.string(),
    /** The tabs this composite belongs on. */
    tabs: z.array(z.string()),
    /** Suffix for a day in `series`, so any day reads in the card's own units. */
    unit: z.string(),
});
export const snapshotDetailSchema = z.object({
    capturedAt: z.string(),
    sharpest: snapshotSharpestSchema.nullable(),
    drills: z.record(snapshotDrillIdSchema, snapshotDrillSchema),
    sources: z.array(snapshotSourceDetailSchema),
    /** Weather the week happened in. Context, never a cause. */
    conditions: z.array(snapshotConditionSchema),
    /** Several readings said as one number, with the days behind it. */
    composites: z.array(snapshotCompositeSchema),
    /** Metrics more than one device reported on the same day. */
    conflicts: z.array(snapshotConflictSchema),
    confounders: z.array(snapshotConfounderSchema),
    /** Comparisons the member's own days can settle; empty until there are enough days. */
    findings: z.array(snapshotFindingSchema),
    /** One entry per behaviour with a measurable daily target; the rest are omitted. */
    levels: z.array(snapshotLevelSchema),
    /** The outcome the member is working on, measured against their own days. */
    goal: snapshotGoalSchema.nullable(),
    /**
     * How far along the member's own baseline is. Below `need` days, bands are
     * withheld rather than shown half-true, and the page says so.
     */
    baseline: z.object({ day: z.number().int(), need: z.number().int() }),
    /**
     * Where the member is in their medication cycle, when they take one. A dose
     * moves appetite, sleep and heart rate for days afterwards, so a week read
     * without it is a week read wrong.
     */
    dose: z.object({ days: z.number().int(), label: z.string() }).nullable(),
    /** The behaviours Heartbeat has a moment for, for this member. */
    watches: z.array(healthBehaviourSchema).optional(),
    /** How much of what Snapshot can read is actually reporting. */
    coverage: z.object({
        reporting: z.number().int(),
        total: z.number().int(),
        /** Kinds no connected source claims at all, named so the gap is actionable. */
        missing: z.array(z.string()),
        /** A connected source claims these and has not sent one yet. */
        awaited: z.array(z.string()).optional(),
        /** The same count over the kinds a week cannot be read without. */
        core: z.object({ reporting: z.number().int(), total: z.number().int() }).optional(),
    }),
});
/**
 * How many days of history the Snapshot reads. Every band, verdict and finding
 * is computed inside this window, so it is also the depth a newly connected
 * device is asked to backfill: a member who already wore a ring should meet a
 * page built from their own months, not one that says "usual range forming".
 */
export const SNAPSHOT_SERIES_DAYS = 90;
export const SNAPSHOT_BAND_DAYS = 30;
/*
 * A range needs a fortnight of the member's own days behind it. Five was
 * enough arithmetic to draw a band and not enough life to mean one: it put a
 * confident "your usual" under a line five days long. Below this the band is
 * withheld and the chart says how many days are left.
 */
export const SNAPSHOT_BAND_MIN_POINTS = 14;
/**
 * The member's own band: the middle 60% of the prior days, today excluded. A
 * baseline that contains the value being judged against it is circular, so the
 * last point never enters its own band.
 */
export function snapshotBand(series, days = SNAPSHOT_BAND_DAYS) {
    const prior = series
        .slice(0, -1)
        .slice(-days)
        .filter((v) => typeof v === "number" && Number.isFinite(v))
        .sort((a, b) => a - b);
    if (prior.length < SNAPSHOT_BAND_MIN_POINTS)
        return null;
    const at = (q) => prior[Math.min(prior.length - 1, Math.floor(prior.length * q))] ?? 0;
    return { lo: at(0.2), hi: at(0.8), med: at(0.5) };
}
/** Where a day sat, in the member's own words, for a chart or a readout. */
const VERDICT_PHRASE = {
    over: "over your usual",
    under: "under your usual",
    in: "inside your usual",
};
export function snapshotVerdictPhrase(verdict) {
    return verdict === null ? null : VERDICT_PHRASE[verdict];
}
export function snapshotVerdict(value, band) {
    if (value === null || band === null)
        return null;
    if (value < band.lo)
        return "under";
    if (value > band.hi)
        return "over";
    return "in";
}
/** How many more days of readings a range still needs. */
export function bandDaysRemaining(series) {
    const prior = series
        .slice(0, -1)
        .slice(-SNAPSHOT_BAND_DAYS)
        .filter((v) => typeof v === "number" && Number.isFinite(v)).length;
    return Math.max(0, SNAPSHOT_BAND_MIN_POINTS - prior);
}
/**
 * How much of a week the mean needs before it will state one. A single day is
 * not a mean of anything, so the line stops rather than tracing the raw series
 * and calling it a seven-day read.
 */
const SMOOTH_MIN_POINTS = 2;
/**
 * A 7-day centred mean, so a daily series draws as a line instead of a saw.
 *
 * The mean is a statement about the week around a day, not about that day, so
 * a day the member's device did not report still has one as long as the week
 * around it did. Nulling it there broke the line into dashes for anyone whose
 * wearable misses a night, which is most people; the chart already shades the
 * days nothing reported, so the absence is stated without also cutting the
 * only line on the plot into pieces.
 */
export function smoothSeries(series) {
    // The mean is only defined across the reported span. Without these bounds it
    // reaches up to three days either side of the first and last reading, so the
    // line drew straight through the stretch the chart labels as having no
    // readings at all.
    const first = series.findIndex((v) => v !== null);
    if (first < 0)
        return series.map(() => null);
    let last = series.length - 1;
    while (last > first && series[last] === null)
        last--;
    return series.map((_, i) => {
        if (i < first || i > last)
            return null;
        const win = series
            .slice(Math.max(0, i - 3), Math.min(series.length, i + 4))
            .filter((x) => x !== null);
        if (win.length < SMOOTH_MIN_POINTS)
            return null;
        return win.reduce((a, b) => a + b, 0) / win.length;
    });
}
/** Where a value sits on a band track, in percent, with the domain padded so the band reads as a range. */
export function placeInBand(value, band) {
    const span = Math.max(band.hi - band.lo, 1e-9);
    const min = band.lo - span * 0.7;
    const max = band.hi + span * 0.7;
    const pct = (v) => Math.max(0, Math.min(100, ((v - min) / (max - min)) * 100));
    return {
        lo: pct(band.lo),
        hi: pct(band.hi),
        pos: pct(value),
        out: value < band.lo || value > band.hi,
    };
}
/**
 * How far outside its own band a reading sits, in band widths. In-band
 * readings score 0, so nothing in range can ever be called the sharpest.
 */
/**
 * Distance is measured from the verdict, so a reading with none cannot lead.
 * That is how a day still being counted stays out of the hero: steps, active
 * and vigorous minutes carry no verdict once the device has reported today, so
 * from the first sync of the day they are unrankable and the page leads on
 * something whole. Before that sync the value on show is yesterday's, which is
 * a finished day and ranks normally. The rule is the same either way: judge
 * the day on show.
 */
export function bandDistance(reading) {
    const { band, value, verdict } = reading;
    if (!band || value === null || verdict === null || verdict === "in")
        return 0;
    const width = band.hi - band.lo;
    const span = width > 0 ? width : Math.abs(band.med) || 1;
    return verdict === "under" ? (band.lo - value) / span : (value - band.hi) / span;
}
/**
 * The readings allowed to lead the page. Distance is normalised by band width,
 * so without this the tightest band wins and the hero announces "Awake was
 * 0h 18m, your usual is 0h 14m to 0h 16m", or a blood pressure with no clinical
 * framing. A reading leads only if a member would act on it.
 */
export const SNAPSHOT_LEAD_KEYS = new Set([
    "sleep_hours",
    "deep",
    "rem",
    "efficiency",
    "hrv",
    "rhr",
    "stress",
    "steps",
    "active_min",
    "vigorous",
]);
export function pickSharpest(candidates) {
    let best = null;
    for (const c of candidates) {
        if (!SNAPSHOT_LEAD_KEYS.has(c.reading.key))
            continue;
        const d = bandDistance(c.reading);
        if (d > 0 && (best === null || d > best.d))
            best = { d, c };
    }
    return best ? { ...best.c.reading, drill: best.c.drill } : null;
}
/**
 * One line naming what the week was like around a tab's readings. Composed from
 * the conditions themselves rather than authored, so it can only ever say what
 * the page already shows, and it stays silent when there is nothing to say.
 */
export function snapshotConditionsSay(conditions, tab) {
    const on = conditions.filter((c) => c.tabs.includes(tab));
    if (on.length === 0)
        return null;
    const named = on.slice(0, 2).map((c) => `${c.label.toLowerCase()} ${c.value}`);
    return `${joinConditions(named)}. Context, never a cause.`;
}
/** Drills whose reading is taken overnight, so their run is counted in nights. */
const NIGHTLY_DRILLS = new Set(["sleep", "recovery"]);
/** The run of settled days ending at the newest one, counted once so the chart and the standing line cannot disagree about the member's own history. */
export function verdictRun(verdicts) {
    // Newest first.
    const from = verdicts[0] === null ? 1 : 0;
    const latest = verdicts[from];
    if (!latest)
        return null;
    let days = 0;
    while (verdicts[from + days] === latest)
        days++;
    return { verdict: latest, days };
}
/** How the week's measured behaviours sit against the member's own usual, with `**` around the part to emphasise so each client uses its own renderer. */
export function snapshotTallySay(inRange, graded) {
    if (graded === 0)
        return "Nothing measured yet";
    if (graded === 1)
        return `Your one measured area is **${inRange === 1 ? "in" : "outside"}** your usual range`;
    const count = inRange === graded ? `All ${graded}` : `${inRange} of ${graded}`;
    return `**${count}** measured areas are in your usual range`;
}
/**
 * Whether the reading on show is a blip or a pattern, in one clause.
 *
 * The headline already carries the number, the window and where it sits, so
 * the one thing a member cannot read off it quickly is whether last night was
 * unusual or the fourth of its kind. That is the question behind "is this
 * bad", and it is the whole job of this line: no number, since repeating one
 * from a different window is how the tab came to disagree with itself, and no
 * instruction, since the level card beside it already names the habit.
 *
 * Silent until there is a run worth naming, and silent on a day still being
 * counted, which has no verdict to extend.
 */
export function snapshotStandingSay(drill) {
    const verdict = drill.headline.verdict;
    if (verdict === null)
        return null;
    // Two days cannot show a pattern, and calling one a run would invent it.
    // Counted on days that actually carry a verdict:
    if (drill.days.filter((d) => d.verdict !== null).length < 3)
        return null;
    // Of the headline's own verdict.
    const latest = verdictRun(drill.days.map((d) => d.verdict));
    if (latest === null || latest.verdict !== verdict)
        return null;
    const run = latest.days;
    if (run === 0)
        return null;
    if (run === 1)
        return `Just the one ${NIGHTLY_DRILLS.has(drill.id) ? "night" : "day"} so far.`;
    // One wording of a run for the whole product, from the table that knows what a reading of this kind covers.
    const phrase = streakPhrase(NIGHTLY_DRILLS.has(drill.id) ? "wearable.sleep" : "wearable.activity", run);
    return phrase ? `${phrase.charAt(0).toUpperCase()}${phrase.slice(1)}.` : null;
}
function joinConditions(parts) {
    if (parts.length === 0)
        return "";
    if (parts.length === 1)
        return capitaliseFirst(parts[0] ?? "");
    return capitaliseFirst(`${parts.slice(0, -1).join(", ")} and ${parts.at(-1) ?? ""}`);
}
const capitaliseFirst = (s) => s.charAt(0).toUpperCase() + s.slice(1);
/** The reading as it stood a given number of days back, read off the same days array the band was measured from. */
export function snapshotReadingOnDay(reading, daysAgo) {
    const recent = reading.recent;
    if (!recent)
        return daysAgo === 0
            ? reading
            : { ...reading, value: null, display: null, verdict: null, inProgress: false };
    const day = recent[daysAgo];
    return {
        ...reading,
        value: day?.value ?? null,
        display: day?.display ?? null,
        verdict: day?.verdict ?? null,
        inProgress: daysAgo === 0 ? reading.inProgress === true : false,
    };
}
/** Every index in `recent` where at least one of these readings reported. */
function reportedDays(readings) {
    const days = new Set();
    for (const r of readings) {
        const recent = r.recent ?? [];
        const end = Math.min(recent.length, SNAPSHOT_READING_RECENT_DAYS);
        for (let i = 0; i < end; i++) {
            if (recent[i]?.value !== null && recent[i]?.value !== undefined)
                days.add(i);
        }
    }
    return [...days].sort((a, b) => a - b);
}
/** The day the breakdown opens on: */
export function snapshotReadingLatestDay(readings) {
    return reportedDays(readings)[0] ?? 0;
}
/** Whether any of these reported inside the recent window at all. */
export function snapshotReadingHasDays(readings) {
    return reportedDays(readings).length > 0;
}
/** The reading as it stood `daysAgo`, or as itself when it reports less often than the cursor steps. */
export function snapshotReadingAtDay(reading, daysAgo) {
    return snapshotReadingHasDays([reading]) ? snapshotReadingOnDay(reading, daysAgo) : reading;
}
/** How far back the breakdown can step: */
export function snapshotReadingDaysBack(readings) {
    const days = reportedDays(readings);
    return days.length === 0 ? 0 : (days[days.length - 1] ?? 0);
}
/** What the day the cursor sits on is called in the heading above it: */
export function snapshotDayHeading(drill, at, from, hasDays, locale) {
    if (!hasDays || at === from)
        return drill.window;
    return snapshotDayLabel(drill.days[at]?.day, at, locale);
}
/** The member's own week rather than a date: "Today", "Yesterday", "Tuesday 9 Sep". */
export function snapshotDayLabel(key, daysAgo, locale) {
    if (daysAgo === 0)
        return "Today";
    if (daysAgo === 1)
        return "Yesterday";
    if (key === undefined)
        return `${daysAgo} days ago`;
    const d = new Date(`${key}T12:00:00Z`);
    if (Number.isNaN(d.getTime()))
        return key;
    // Formatted in UTC because the key is already a local calendar date.
    return d.toLocaleDateString(locale, {
        timeZone: "UTC",
        weekday: "long",
        day: "numeric",
        month: "short",
    });
}
//# sourceMappingURL=snapshot-detail.js.map